// src/pages/DriverQueue.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import Swal from "sweetalert2";
import parseStringDate from "../utils/parseStringDate";
import { updateCalendar } from "../services/calendarService";
import { removeDirectDriverFromJob } from "../services/jobService";
import {
  reconcileDriverQueue,
  updateDriverQueueOrder,
} from "../services/driverQueueService";
import { getEventTypeStyle } from "../utils/eventTypeStyles";

/**
 * QueueRow — one reorderable item in the driver's unified queue.
 *
 * An item is either a calendar event (`kind: 'event'`) or a direct
 * assignment (`kind: 'direct'`). The two render slightly differently
 * — direct items show no date/time and a small "Direct assignment"
 * badge, while events show the usual date, time, and eventType pill.
 */
const QueueRow = ({ item, onDragEnd, onUnassign }) => {
  const controls = useDragControls();
  const isEvent = item.kind === "event";
  const data = item.data || {};

  // Common card pieces (computed differently per kind).
  let badgeText = "—";
  let badgeStyle = "bg-gray-100 text-gray-800";
  let borderLeftClass = "border-l-4 border-l-gray-300";
  let dateLine = "";
  let title = data.title || "(no title)";
  let customerName = data.customerName || "";
  let customerPhone = data.customerPhone || "";
  let location = data.location || "";
  let isDone = false;

  if (isEvent) {
    const start = data.start ? parseStringDate(data.start) : null;
    if (start) {
      const day = start.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      const time = start.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
      dateLine = `${day} · ${time}`;
    }
    // Canonical per-type colour from the shared palette.
    // See src/utils/eventTypeStyles.js for the full mapping (10 types).
    const s = getEventTypeStyle(data.eventType);
    badgeText = data.eventType || "event";
    badgeStyle = `${s.bgIcon} ${s.textStrong}`;
    borderLeftClass = s.borderLeft;
    isDone = data.status === "done";
  } else {
    // Direct-assigned job — no schedule, fall back to job-level fields.
    badgeText = "direct";
    badgeStyle = "bg-purple-100 text-purple-800";
    borderLeftClass = "border-l-4 border-l-purple-500";
    dateLine = "no scheduled time";
    title = data.customerName || data.address || `Job ${data.id?.slice(0, 6)}…`;
    customerPhone = data.customerPhone || "";
    location = data.address || "";
    customerName = data.customerName || "";
  }

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      className={`border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 cursor-default ${borderLeftClass}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 p-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 4a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm6-15a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeStyle}`}
            >
              {badgeText}
            </span>
            {dateLine && (
              <span className="text-sm text-gray-500">{dateLine}</span>
            )}
            {isDone && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                ✓ done
              </span>
            )}
          </div>
          <h4 className="font-medium text-gray-900 truncate">{title}</h4>
          {customerName && (
            <p className="text-sm text-gray-600 truncate">
              {customerName}
              {customerPhone ? ` · ${customerPhone}` : ""}
            </p>
          )}
          {location && (
            <p className="text-sm text-gray-500 truncate">📍 {location}</p>
          )}
        </div>

        {/* Unassign — removes the fitter from this event/job. The
            calendar event itself stays in the calendar (just loses
            its driver); a direct-assigned job loses this fitter from
            directDriverIds. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnassign?.(item);
          }}
          className="ml-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded -mr-1"
          title="Unassign from this fitter"
          aria-label="Unassign from this fitter"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </Reorder.Item>
  );
};

/**
 * DriverQueue — admin page at /drivers/:driverId/queue.
 *
 * Renders a single ordered list of EVERYTHING assigned to this fitter
 * (calendar events + direct-assigned jobs). The order is stored on
 * `drivers/{uid}.queueOrder` and persists across reloads. Drag any row
 * to reorder; the new order is written on dragEnd.
 *
 * The list also includes a "Completed" section below for any calendar
 * events with status='done'. Direct-assigned jobs have no done state.
 */
const DriverQueue = () => {
  const { driverId } = useParams();
  const [driver, setDriver] = useState(null);
  const [items, setItems] = useState([]); // pending items in queue order
  const [completed, setCompleted] = useState([]); // done events, not reorderable
  const [loading, setLoading] = useState(true);
  const dirtyRef = useRef(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { driver: d, items: all } = await reconcileDriverQueue(driverId);
      setDriver(d);
      // Pending = everything except done events. Direct items never count
      // as done — there's no event to mark complete.
      const pending = [];
      const done = [];
      for (const it of all) {
        if (it.kind === "event" && it.data?.status === "done") {
          done.push(it);
        } else {
          pending.push(it);
        }
      }
      setItems(pending);
      setCompleted(done);
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const handleReorder = (newOrder) => {
    setItems(newOrder);
    dirtyRef.current = true;
  };

  /**
   * Unassign an item from this fitter.
   *
   * - kind:'event'  → clear driverId + driverSortOrder on the calendar
   *   event. The event itself stays; only the assignment goes away.
   *   _recalcJobAssignedDrivers fires from updateCalendar and removes
   *   the fitter from the underlying job.assignedDriverIds.
   * - kind:'direct' → remove fitter UID from job.directDriverIds.
   *
   * After either action, refresh() re-runs reconcileDriverQueue which
   * drops the now-orphaned entry from drivers/{uid}.queueOrder.
   *
   * Optimistic UI: remove from local items immediately. On error,
   * restore by re-fetching.
   */
  const handleUnassign = async (item) => {
    const labelKind = item.kind === "event" ? "this calendar event" : "this direct assignment";
    const titleName = item.data?.title || item.data?.customerName || "this item";
    const result = await Swal.fire({
      title: `Unassign ${titleName}?`,
      text:
        item.kind === "event"
          ? `Removes ${driver?.name || "the fitter"} from this calendar event. The event stays in the calendar — only the fitter assignment is cleared.`
          : `Removes ${driver?.name || "the fitter"} from this job's direct assignments. The job is unaffected.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, unassign",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    // Optimistic remove from the visible list.
    setItems((prev) =>
      prev.filter((it) => !(it.kind === item.kind && it.id === item.id))
    );

    try {
      if (item.kind === "event") {
        await updateCalendar(item.id, {
          driverId: null,
          driverSortOrder: null,
        });
      } else {
        await removeDirectDriverFromJob(item.id, driverId);
      }
      // Background: reconciliation cleans up queueOrder on the next
      // mount/refresh. Call refresh() to trigger now so the count in
      // the header ("N pending") updates and queueOrder is tidy.
      refresh();
    } catch (err) {
      console.error("Failed to unassign", err);
      Swal.fire({
        title: "Couldn't unassign",
        text: err.message || String(err),
        icon: "error",
      });
      refresh(); // restore authoritative state
    }
  };

  const flushReorder = async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      // queueOrder stores BOTH pending and completed in the same array
      // (so reordering pending doesn't lose track of completed). Rebuild
      // the full array preserving completed at the bottom.
      const fullOrder = [...items, ...completed].map(({ kind, id }) => ({
        kind,
        id,
      }));
      await updateDriverQueueOrder(driverId, fullOrder);

      // Backwards-compatibility: also write driverSortOrder back onto
      // each event so legacy calendar views that still read it (e.g.
      // calendar event cards) stay consistent with the unified order.
      // Only event items have a calendar doc to update.
      let i = 0;
      for (const it of items) {
        if (it.kind === "event") {
          await updateCalendar(it.id, { driverSortOrder: i });
          i += 1;
        }
      }
    } catch (err) {
      console.error("Failed to persist driver queue order", err);
      Swal.fire({
        title: "Couldn't save",
        text: "The new order didn't save. Try again.",
        icon: "error",
      });
      refresh();
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-gray-700">Fitter not found.</p>
        <Link
          to="/drivers"
          className="text-indigo-600 hover:text-indigo-800 text-sm"
        >
          ← Back to fitters
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div>
        <Link
          to="/drivers"
          className="text-sm text-indigo-600 hover:text-indigo-800"
        >
          ← Back to fitters
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {driver.name}'s queue
        </h1>
        <p className="text-gray-600 mt-1">
          {items.length} pending · {completed.length} completed
        </p>
        {driver.disabled && (
          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
            This fitter is currently disabled. They can still appear here
            until events are reassigned.
          </div>
        )}
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Pending queue
        </h2>
        <p className="text-sm text-gray-500 mb-3">
          Drag rows to set the order this fitter sees in their app. Calendar
          events and direct-assigned jobs are mixed together — the order
          here is the order they see.
        </p>
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No pending work assigned to this fitter yet. Assign one from the
            calendar event modal or via "Assign fitter" on a customer's job
            page.
          </p>
        ) : (
          <Reorder.Group
            axis="y"
            values={items}
            onReorder={handleReorder}
            className="space-y-3"
          >
            {items.map((it) => (
              <QueueRow
                key={`${it.kind}:${it.id}`}
                item={it}
                onDragEnd={flushReorder}
                onUnassign={handleUnassign}
              />
            ))}
          </Reorder.Group>
        )}
      </section>

      {completed.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Completed
          </h2>
          <div className="space-y-3">
            {completed.map((it) => {
              const e = it.data;
              const start = e.start ? parseStringDate(e.start) : null;
              return (
                <div
                  key={`${it.kind}:${it.id}`}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-80"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                      ✓ done
                    </span>
                    {start && (
                      <span className="text-sm text-gray-500">
                        {start.toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                    )}
                  </div>
                  <h4 className="font-medium text-gray-900 truncate">
                    {e.title || "(no title)"}
                  </h4>
                  {e.customerName && (
                    <p className="text-sm text-gray-600 truncate">
                      {e.customerName}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default DriverQueue;
